import { doodBlockConfig, propertyValue } from './dood-block-config';
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from './descriptor-field-order';
import type { IBlockDescriptor } from './descriptor';

export const SHOPCARDBLOCK_LEGACY_FIELD_ORDER = [
  "shop",
  { type: 'separator' },
  "display_entity_name",
  "display_entity_image",
  "preserve_image_dimensions",
  "image_background_color",
  "display_entity_category",
  { type: 'separator' },
  "entity_name_size",
  { type: 'separator' },
  "entity_name",
  "entity_category",
  "image"
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  shopCardBlockDescriptor: _generatedShopCardBlockDescriptor,
  shopCardBlockBlocDefinition,
  shopCardBlockDashboardLayout,
  shopCardBlockConfig,
  shopCardBlockResolveConfig,
  shopCardBlockProperties,
} = doodBlockConfig(
  {
    name: "Shop card",
    componentType: 'ShopCardBlockComponent',
    category: 'catalog',
    description: "Shop card.",
    keywords: [],
    allowedIn: [
      { bloc: "ShopCardsBlockComponent", slot: "shops" },
    ],
    sharedFields: {}
  },

  () => ({
    shop: propertyValue({
      type: 'shop',
      value: '',
      options: {
        label: "pages.blocks.shop-card.shop.label",
        max: 1
      }
    }),
    display_entity_name: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.shop-card.display-entity-name.label",
        default: true,
      }
    }),
    display_entity_image: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.shop-card.display-entity-image.label",
      }
    }),
    display_entity_category: propertyValue({
      type: 'checkbox',
      value: true,
      options: {
        label: "pages.blocks.shop-card.display-entity-category.label",
        help: "pages.blocks.shop-card.display-entity-category.help",
      }
    }),
    entity_name_size: propertyValue({
      type: 'select',
      value: "lg",
      options: {
        label: "pages.blocks.shop-card.entity-name-size.label",
        choices: [
          {
            value: "xs",
            label: "pages.blocks.shop-card.entity-name-size.options.xs",
          },
          {
            value: "s",
            label: "pages.blocks.shop-card.entity-name-size.options.s",
          },
          {
            value: "m",
            label: "pages.blocks.shop-card.entity-name-size.options.m",
          },
          {
            value: "lg",
            label: "pages.blocks.shop-card.entity-name-size.options.lg",
          },
          {
            value: "2xl",
            label: "pages.blocks.shop-card.entity-name-size.options.2xl",
          },
          {
            value: "3xl",
            label: "pages.blocks.shop-card.entity-name-size.options.3xl",
          }
        ]
      }
    }),
    entity_name: propertyValue({
      type: 'textarea',
      value: '',
      options: {
        label: "pages.blocks.shop-card.entity-name.label",
        help: "pages.blocks.shop-card.entity-name.help",
        rows: 2
      }
    }),
    entity_category: propertyValue({
      type: 'text',
      value: '',
      options: {
        label: "pages.blocks.shop-card.entity-category.label",
        help: "pages.blocks.shop-card.entity-category.help",
      }
    }),
    image: propertyValue({
      type: 'image',
      value: '',
      options: {
        label: "pages.blocks.shop-card.image.label",
        help: "pages.blocks.shop-card.image.help",
      }
    }),
    preserve_image_dimensions: propertyValue({
      type: 'checkbox',
      value: false,
      options: {
        label: "pages.blocks.shop-card.preserve-image-dimensions.label",
        help: "pages.blocks.shop-card.preserve-image-dimensions.help",
      }
    }),
    image_background_color: propertyValue({
      type: 'color',
      value: { type: 'palette', value: 'neutral-100' },
      options: {
        label: "pages.blocks.shop-card.image-background-color.label",
        help: "pages.blocks.shop-card.image-background-color.help",
      }
    })
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.shop-card.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.shop-card.fieldset.shop.label",
            fields: ["shop"],
          },
          {
            legend: "pages.blocks.shop-card.fieldset.name.label",
            fields: [
              "display_entity_name",
              {
                prop: "entity_name",
                visibleWhen: { display_entity_name: true },
              },
              {
                prop: "entity_name_size",
                visibleWhen: { display_entity_name: true },
              },
            ],
          },
          {
            legend: "pages.blocks.shop-card.fieldset.category.label",
            fields: [
              "display_entity_category",
              {
                prop: "entity_category",
                visibleWhen: { display_entity_category: true },
              },
            ],
          },
          {
            legend: "pages.blocks.shop-card.fieldset.image.label",
            fields: [
              "display_entity_image",
              {
                prop: "preserve_image_dimensions",
                visibleWhen: { display_entity_image: true },
              },
              {
                prop: "image_background_color",
                visibleWhen: { display_entity_image: true },
              },
              {
                prop: "image",
                visibleWhen: { display_entity_image: true },
              },
            ],
          },
        ],
      },
    ],
  })
);

export function shopCardBlockDescriptor(): IBlockDescriptor;
export function shopCardBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopCardBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopCardBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(_generatedShopCardBlockDescriptor, shopCardBlockProperties, fieldOrder);
}

export {
  shopCardBlockBlocDefinition,
  shopCardBlockDashboardLayout,
  shopCardBlockProperties,
  shopCardBlockConfig,
  shopCardBlockResolveConfig,
};

export type ShopCardBlockConfig = typeof shopCardBlockConfig;
